// ─── Supported languages ──────────────────────────────────────────────────────
// To add a new language:
//   1. Add an entry below with its code, label, and flag emoji.
//   2. Copy public/locales/_template.json to public/locales/<code>.json
//   3. Translate the values (keep the keys — the English source text — unchanged).
// That's it. No other code changes are required.

export interface LanguageDef {
  code: string;
  label: string;   // Name shown in the language selector (in its own language)
  flag: string;     // Emoji flag shown in the selector
  rtl?: boolean;    // Set true for right-to-left scripts (e.g. Arabic)
}

export const SUPPORTED_LANGUAGES: LanguageDef[] = [
  { code: 'en', label: 'English',    flag: '🇬🇧' },
  { code: 'es', label: 'Español',    flag: '🇪🇸' },
  { code: 'fr', label: 'Français',   flag: '🇫🇷' },
  { code: 'de', label: 'Deutsch',    flag: '🇩🇪' },
  { code: 'ru', label: 'Русский',    flag: '🇷🇺' },
  { code: 'uk', label: 'Українська', flag: '🇺🇦' },
  { code: 'zh', label: '中文',        flag: '🇨🇳' },
  { code: 'ja', label: '日本語',      flag: '🇯🇵' },
  { code: 'ko', label: '한국어',      flag: '🇰🇷' },
  { code: 'ar', label: 'العربية',    flag: '🇸🇦', rtl: true },
  { code: 'pig', label: 'Igpay Atinlay', flag: '🐷' },
];

export const isRtlLanguage = (code: string): boolean =>
  SUPPORTED_LANGUAGES.some(l => l.code === code && l.rtl === true);

export const DEFAULT_LANGUAGE = 'en';

export const isSupportedLanguage = (code: string): boolean =>
  SUPPORTED_LANGUAGES.some(l => l.code === code);

/**
 * Matches a raw browser locale (e.g. "fr-CA", "zh-Hant", "pt-BR") to one of
 * our supported language codes. Falls back to the default language if no
 * supported language matches.
 */
export function detectBrowserLanguage(): string {
  const candidates = (navigator.languages && navigator.languages.length > 0)
    ? navigator.languages
    : [navigator.language];

  for (const raw of candidates) {
    if (!raw) continue;
    const base = raw.toLowerCase().split('-')[0];
    if (isSupportedLanguage(base)) return base;
  }
  return DEFAULT_LANGUAGE;
}
